import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface GoogleAdsCampaign {
  google_ads_campaign_id: string;
  name: string;
  status: 'ENABLED' | 'PAUSED' | 'UNKNOWN' | string;
  start_date: string | null;
  end_date: string | null;
  budget_micros: number | null;
  cost_micros_30d: number;
  clicks_30d: number;
}

export type SyncAction =
  | 'created'
  | 'adopted_by_id'
  | 'adopted_by_name'
  | 'updated'
  | 'archived';

export interface SyncRow {
  google_ads_campaign_id: string;
  google_name: string;
  status: string;
  campaign_id: string;
  local_name: string;
  action: SyncAction;
}

export interface SyncSummary {
  created: number;
  adopted_by_id: number;
  adopted_by_name: number;
  updated: number;
  archived: number;
  unseen_legacy: number;
  rows: SyncRow[];
}

export interface SyncCampaignsResult {
  ok: true;
  customer_id: string;
  currency_code: string | null;
  account_timezone: string | null;
  campaigns: GoogleAdsCampaign[];
  sync: SyncSummary;
}

export interface LinkedCampaign {
  id: string;
  name: string;
  google_ads_campaign_id: string | null;
  google_ads_status: string | null;
  is_active: boolean;
}

/** A local campaign with no Google link: a legacy / auto-created row that may belong to a Google campaign. */
export interface UnlinkedCampaign {
  id: string;
  name: string;
  tracking_number: string | null;
  external_campaign_id: string | null;
  is_active: boolean;
  lead_count: number;
}

export interface SyncSpendResult {
  ok: true;
  from_date: string;
  to_date: string;
  campaigns_synced: number;
  rows_upserted: number;
  total_cost: number;
  currency: string;
  skipped_unmatched_rows: number;
  message?: string;
}

const LINKED_KEY = ['campaigns', 'google-linked'] as const;
const UNLINKED_KEY = ['campaigns', 'google-unlinked'] as const;

async function invokeDetail(error: unknown, fallback: string): Promise<never> {
  let detail = (error as { message?: string })?.message ?? fallback;
  const ctx = (error as { context?: Response })?.context;
  if (ctx && typeof ctx.json === 'function') {
    try {
      const body = (await ctx.json()) as { error?: string; detail?: string };
      detail = body.detail || body.error || detail;
    } catch {/* ignore */}
  }
  throw new Error(detail);
}

export function useGoogleAdsCampaigns() {
  const queryClient = useQueryClient();

  const invalidateCampaigns = () => {
    queryClient.invalidateQueries({
      predicate: (q) => typeof q.queryKey[0] === 'string' && (q.queryKey[0] as string).startsWith('campaign'),
    });
    queryClient.invalidateQueries({ queryKey: ['reports', 'campaign-report'] });
  };

  // Campaigns already linked to Google — the merge targets.
  const linkedQuery = useQuery<LinkedCampaign[]>({
    queryKey: LINKED_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campaigns')
        .select('id, name, google_ads_campaign_id, google_ads_status, is_active')
        .not('google_ads_campaign_id', 'is', null)
        .order('name');
      if (error) throw error;
      return (data ?? []) as LinkedCampaign[];
    },
  });

  // Active campaigns with no Google link, with how many leads sit on them.
  const unlinkedQuery = useQuery<UnlinkedCampaign[]>({
    queryKey: UNLINKED_KEY,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('campaigns')
        .select('id, name, tracking_number, external_campaign_id, is_active, leads(count)')
        .is('google_ads_campaign_id', null)
        .eq('is_active', true)
        .order('name');
      if (error) throw error;
      return ((data ?? []) as Array<Record<string, unknown>>).map((row) => {
        const leads = row.leads as Array<{ count: number }> | undefined;
        return {
          id: row.id as string,
          name: row.name as string,
          tracking_number: (row.tracking_number as string | null) ?? null,
          external_campaign_id: (row.external_campaign_id as string | null) ?? null,
          is_active: row.is_active as boolean,
          lead_count: leads?.[0]?.count ?? 0,
        };
      });
    },
  });

  // Pull + auto-map. A mutation, not a query: explicit user action with a
  // real cost (token refresh + 2 Google calls + writes), never auto-refetched.
  const syncMutation = useMutation<SyncCampaignsResult, Error, void>({
    mutationFn: async () => {
      const { data, error } = await supabase.functions.invoke('google-ads-sync-campaigns', { body: {} });
      if (error) await invokeDetail(error, 'sync_failed');
      return data as SyncCampaignsResult;
    },
    onSuccess: invalidateCampaigns,
  });

  // Fold a legacy row into its Google campaign (leads, calls, texts, spend move; the row is backed up then deleted).
  const mergeMutation = useMutation<unknown, Error, { from: string; into: string }>({
    mutationFn: async ({ from, into }) => {
      const { data, error } = await supabase.rpc('merge_campaign', { _from: from, _into: into });
      if (error) throw new Error(error.message);
      return data;
    },
    onSuccess: invalidateCampaigns,
  });

  // Pull daily spend for Google-linked campaigns. Empty body = catch up from
  // the last synced day (or backfill); { from_date, to_date } overrides.
  const syncSpendMutation = useMutation<SyncSpendResult, Error, { from_date?: string; to_date?: string } | undefined>({
    mutationFn: async (input) => {
      const body: Record<string, string> = {};
      if (input?.from_date) body.from_date = input.from_date;
      if (input?.to_date) body.to_date = input.to_date;
      const { data, error } = await supabase.functions.invoke('google-ads-sync-spend', { body });
      if (error) await invokeDetail(error, 'sync_spend_failed');
      return data as SyncSpendResult;
    },
    onSuccess: invalidateCampaigns,
  });

  return {
    linkedCampaigns: linkedQuery.data ?? [],
    unlinkedCampaigns: unlinkedQuery.data ?? [],
    isLoadingCampaigns: linkedQuery.isLoading || unlinkedQuery.isLoading,
    sync: syncMutation.mutateAsync,
    isSyncing: syncMutation.isPending,
    syncResult: syncMutation.data ?? null,
    syncError: syncMutation.error,
    merge: mergeMutation.mutateAsync,
    isMerging: mergeMutation.isPending,
    syncSpend: (input?: { from_date?: string; to_date?: string }) => syncSpendMutation.mutateAsync(input),
    isSyncingSpend: syncSpendMutation.isPending,
    spendResult: syncSpendMutation.data ?? null,
  };
}
